import { format, parseISO } from "date-fns";
import DateTimeHelper from "../../../core/DateTimeHelper";

const dateTimeHelper = new DateTimeHelper();

/*
**  Holiday dates follow the same pattern as Stats DatePicker:
**  - Native date inputs display in the browser/user locale
**  - Values in the UI are local calendar dates
**  - API stores/returns UTC; convert on the way in and out
*/

export function toDateInputValue(date) {
  if (!date) return "";
  const d = date instanceof Date ? date : new Date(date);
  return format(d, "yyyy-MM-dd");
}

export function parseDateInputValue(value) {
  if (!value) return null;
  try {
    return parseISO(value);
  } catch {
    return null;
  }
}

/** Local calendar date (00:00 local) → UTC ISO string for API posts/queries. */
export function localDateToUtcIso(date) {
  if (!date) return null;
  const d = date instanceof Date ? date : parseISO(date);
  const localMidnight = new Date(d.getFullYear(), d.getMonth(), d.getDate(), 0, 0, 0, 0);
  return localMidnight.toISOString();
}

/** UTC string/Date from API → local calendar Date (00:00 local of that instant's local day). */
export function utcToLocalDate(utc) {
  if (!utc) return null;
  const utcDate =
    typeof utc === "string"
      ? dateTimeHelper.convertUtcStringToUtcDateTime(utc)
      : utc;
  return new Date(utcDate.getFullYear(), utcDate.getMonth(), utcDate.getDate());
}

/** Inclusive local-day overlap check for calendar cells. */
export function holidayCoversLocalDay(holiday, localDay) {
  const start = utcToLocalDate(holiday.startDateTime);
  const end = utcToLocalDate(holiday.endDateTime);
  if (!start || !end) return false;
  const dayTime = localDay.getTime();
  return dayTime >= start.getTime() && dayTime <= end.getTime();
}

/**
 * True when a leave record overlaps any local day in [rangeStart, rangeEnd]
 * (inclusive). Covers starts/ends inside the range and spans that begin before
 * and/or end after it.
 */
export function recordOverlapsLocalRange(record, rangeStart, rangeEnd) {
  const start = utcToLocalDate(record.startDateTime);
  const end = utcToLocalDate(record.endDateTime);
  if (!start || !end || !rangeStart || !rangeEnd) return false;
  const rangeStartTime = startOfLocalDay(rangeStart).getTime();
  const rangeEndTime = startOfLocalDay(rangeEnd).getTime();
  return end.getTime() >= rangeStartTime && start.getTime() <= rangeEndTime;
}

function startOfLocalDay(date) {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

/**
 * Split holidays into week segments for continuous multi-day bars.
 * weekDays: array of 7 local Dates (Mon–Sun).
 * Returns segments with startCol/endCol (0–6), lane, and edge flags for rounding.
 */
export function buildWeekHolidaySegments(weekDays, holidays) {
  if (!weekDays?.length || weekDays.length !== 7) return [];

  const weekStart = startOfLocalDay(weekDays[0]);
  const weekEnd = startOfLocalDay(weekDays[6]);
  const dayTimes = weekDays.map((d) => startOfLocalDay(d).getTime());

  const segments = [];
  holidays.forEach((holiday) => {
    const holidayStart = utcToLocalDate(holiday.startDateTime);
    const holidayEnd = utcToLocalDate(holiday.endDateTime);
    if (!holidayStart || !holidayEnd) return;
    if (holidayEnd.getTime() < weekStart.getTime() || holidayStart.getTime() > weekEnd.getTime()) {
      return;
    }

    const segStartTime = Math.max(holidayStart.getTime(), weekStart.getTime());
    const segEndTime = Math.min(holidayEnd.getTime(), weekEnd.getTime());
    const startCol = dayTimes.indexOf(segStartTime);
    const endCol = dayTimes.indexOf(segEndTime);
    if (startCol < 0 || endCol < 0) return;

    segments.push({
      holiday,
      startCol,
      endCol,
      continuesBefore: holidayStart.getTime() < weekStart.getTime(),
      continuesAfter: holidayEnd.getTime() > weekEnd.getTime(),
    });
  });

  segments.sort((a, b) => {
    if (a.startCol !== b.startCol) return a.startCol - b.startCol;
    return b.endCol - b.startCol - (a.endCol - a.startCol);
  });

  const laneEnds = [];
  segments.forEach((segment) => {
    let lane = laneEnds.findIndex((endCol) => endCol < segment.startCol);
    if (lane === -1) {
      lane = laneEnds.length;
      laneEnds.push(segment.endCol);
    } else {
      laneEnds[lane] = segment.endCol;
    }
    segment.lane = lane;
  });

  return segments;
}

export function chunkIntoWeeks(calendarDays) {
  const weeks = [];
  for (let i = 0; i < calendarDays.length; i += 7) {
    weeks.push(calendarDays.slice(i, i + 7));
  }
  return weeks;
}

function startOfLocalDayFromParts(year, month, day) {
  return new Date(year, month, day);
}

/** Day value for a single calendar day within a holiday. */
export function getHolidayDayValue(holiday) {
  return holiday?.durationType === "PartDay" ? 0.5 : 1;
}

/** Format leave day totals: whole numbers without decimals, otherwise one dp. */
export function formatLeaveDays(value) {
  const n = Math.round((Number(value) || 0) * 10) / 10;
  return Number.isInteger(n) ? String(n) : n.toFixed(1);
}

/** Format duration minutes as H:MM (e.g. 120 → "2:00", 456 → "7:36"). */
export function formatDurationMins(mins) {
  const n = Math.max(0, Math.round(Number(mins) || 0));
  const hours = Math.floor(n / 60);
  const minutes = n % 60;
  return `${hours}:${String(minutes).padStart(2, "0")}`;
}

/** Parse H:MM / HH:MM into minutes. Returns null if invalid. */
export function parseDurationMins(value) {
  if (value == null || String(value).trim() === "") return null;
  const parts = String(value).trim().split(":");
  if (parts.length !== 2) return null;
  const hours = Number(parts[0]);
  const minutes = Number(parts[1]);
  if (!Number.isInteger(hours) || !Number.isInteger(minutes)) return null;
  if (hours < 0 || minutes < 0 || minutes > 59) return null;
  const total = hours * 60 + minutes;
  return total > 0 ? total : null;
}

/** Default Other duration: inclusive calendar days × 8 hours. */
export function defaultOtherDurationMins(startDate, endDate) {
  if (!startDate || !endDate) return 8 * 60;
  const start = startOfLocalDay(startDate);
  const end = startOfLocalDay(endDate);
  const dayMs = 24 * 60 * 60 * 1000;
  const days = Math.floor((end.getTime() - start.getTime()) / dayMs) + 1;
  return Math.max(1, days) * 8 * 60;
}

/**
 * Returns true if the user already has holiday/sickness/other leave overlapping
 * [rangeStart, rangeEnd] (local days). Optionally exclude a record being edited.
 */
export function hasConflictingLeave({
  appUserId,
  rangeStart,
  rangeEnd,
  holidays = [],
  sicknesses = [],
  others = [],
  excludeHolidayId = null,
  excludeSicknessId = null,
  excludeOtherId = null,
}) {
  if (!appUserId || !rangeStart || !rangeEnd) return false;

  const overlapsUserRecord = (record, excludeId) => {
    if (String(record.appUserId) !== String(appUserId)) return false;
    if (excludeId != null && record.id === excludeId) return false;
    return recordOverlapsLocalRange(record, rangeStart, rangeEnd);
  };

  const activeHolidays = (holidays || []).filter(
    (h) => String(h.status || "Approved").toLowerCase() !== "declined"
  );

  if (activeHolidays.some((h) => overlapsUserRecord(h, excludeHolidayId))) {
    return true;
  }
  if ((sicknesses || []).some((s) => overlapsUserRecord(s, excludeSicknessId))) {
    return true;
  }
  if ((others || []).some((o) => overlapsUserRecord(o, excludeOtherId))) {
    return true;
  }
  return false;
}

/** True if the user has a Pending/Approved holiday overlapping the range. */
export function hasConflictingHoliday({
  appUserId,
  rangeStart,
  rangeEnd,
  holidays = [],
  excludeHolidayId = null,
}) {
  if (!appUserId || !rangeStart || !rangeEnd) return false;
  return (holidays || []).some((h) => {
    if (String(h.appUserId) !== String(appUserId)) return false;
    if (excludeHolidayId != null && h.id === excludeHolidayId) return false;
    if (String(h.status || "Approved").toLowerCase() === "declined") return false;
    return recordOverlapsLocalRange(h, rangeStart, rangeEnd);
  });
}

/** Holidays that count as booked leave (Approved). */
export function isApprovedHoliday(holiday) {
  return String(holiday?.status || "Approved").toLowerCase() === "approved";
}

/**
 * Summarise a user's leave usage for a calendar year.
 * Holidays may be PartDay (0.5); sickness/other whole-day records count as 1 per day.
 * Returns monthly totals (0–11), taken, remaining, and entitlement.
 */
export function summarizeUserHolidaysForYear(
  holidays,
  userId,
  year,
  entitlement = 28
) {
  const monthly = Array.from({ length: 12 }, () => 0);
  let taken = 0;

  (holidays || [])
    .filter((holiday) => String(holiday.appUserId) === String(userId))
    .forEach((holiday) => {
      const start = utcToLocalDate(holiday.startDateTime);
      const end = utcToLocalDate(holiday.endDateTime);
      if (!start || !end) return;

      const dayValue = getHolidayDayValue(holiday);
      let cursor = startOfLocalDayFromParts(
        start.getFullYear(),
        start.getMonth(),
        start.getDate()
      );
      const endTime = startOfLocalDayFromParts(
        end.getFullYear(),
        end.getMonth(),
        end.getDate()
      ).getTime();

      while (cursor.getTime() <= endTime) {
        if (cursor.getFullYear() === year) {
          monthly[cursor.getMonth()] += dayValue;
          taken += dayValue;
        }
        cursor = startOfLocalDayFromParts(
          cursor.getFullYear(),
          cursor.getMonth(),
          cursor.getDate() + 1
        );
      }
    });

  const remaining =
    entitlement == null ? null : Math.max(0, entitlement - taken);
  return {
    monthly,
    taken,
    remaining,
    entitlement,
  };
}
